import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { T } from "@/components/T";
import { TOOL_REGISTRY } from "@/lib/toolRegistry";
import { PRIMARY_TOOL_IDS } from "@/config/primaryTools";
import { landingPathForTool } from "@/config/toolLandings";

/**
 * "Start here" section — leads with the ten curated primary tools so new
 * visitors see the best of GenieBot without the full 40+ tool gallery
 * overwhelming them. Inline tools deep-link straight into /tools.
 */
const HERO_TOOLS = PRIMARY_TOOL_IDS.map((id) => {
  const tool = TOOL_REGISTRY.find((t) => t.id === id);
  if (!tool) return null;
  const href = tool.route === "/tools" ? `/tools?tool=${tool.id}` : tool.route;
  return {
    id: tool.id,
    name: tool.name,
    description: tool.description,
    href,
    learnTo: landingPathForTool(tool.id),
  };
}).filter((t): t is NonNullable<typeof t> => t !== null);

export function HeroToolsShowcase() {
  return (
    <section
      className="py-16 sm:py-20 px-4 border-y border-border/40"
      aria-labelledby="hero-tools-heading"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10 sm:mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
            <Sparkles className="h-3 w-3" aria-hidden />
            <T>Start here</T>
          </div>
          <h2
            id="hero-tools-heading"
            className="text-3xl sm:text-4xl font-bold text-foreground mb-3"
          >
            <T>Ten tools worth your first ten minutes</T>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            <T>
              The best of GenieBot, hand-picked. Everything is free — pick one and go.
            </T>
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {HERO_TOOLS.map((tool, i) => (
            <motion.div
              key={tool.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: Math.min(i * 0.05, 0.4) }}
            >
              <Link
                to={tool.href}
                className="group flex h-full flex-col rounded-2xl border border-border bg-card/60 p-5 transition-colors hover:border-primary/50 focus-ring"
              >
                <p className="text-sm font-semibold text-foreground group-hover:text-primary leading-snug">
                  {tool.name}
                </p>
                <p className="mt-2 flex-1 text-xs leading-relaxed text-muted-foreground line-clamp-3">
                  {tool.description}
                </p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-primary">
                  <T>Open</T>
                  <ArrowRight
                    className="h-3 w-3 transition-transform group-hover:translate-x-0.5"
                    aria-hidden
                  />
                </p>
              </Link>
              {tool.learnTo && (
                <Link
                  to={tool.learnTo}
                  className="focus-ring mt-2 inline-block text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                >
                  <T>Learn more</T>
                  <span className="sr-only"> about {tool.name}</span>
                </Link>
              )}
            </motion.div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/tools"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary underline-offset-4 hover:underline focus-ring"
          >
            <T>Browse all 40+ tools</T>
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default HeroToolsShowcase;
